import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import './LiksTooltip.css';

export type LiksTooltipPlacement = 'right' | 'top-start' | 'top-end';

export type LiksTooltipProps = {
  content?: string;
  placement?: LiksTooltipPlacement;
  /** Full-width host (e.g. sidebar nav links). */
  block?: boolean;
  /** Expose tooltip text as aria-label on a non-interactive host (e.g. icons). */
  describeChildren?: boolean;
  className?: string;
  children: React.ReactNode;
};

type TooltipCoords = {
  top: number;
  left: number;
};

const VIEWPORT_MARGIN = 8;

function getTooltipCoords(
  rect: DOMRect,
  placement: LiksTooltipPlacement,
): TooltipCoords {
  const gap = 8;

  if (placement === 'right') {
    return {
      top: rect.top + rect.height / 2,
      left: rect.right + gap,
    };
  }

  if (placement === 'top-end') {
    return {
      top: rect.top - gap,
      left: rect.right,
    };
  }

  return {
    top: rect.top - gap,
    left: rect.left,
  };
}

function getClampedCoords(
  coords: TooltipCoords,
  bubbleRect: DOMRect,
): TooltipCoords {
  let dx = 0;
  let dy = 0;

  if (bubbleRect.left < VIEWPORT_MARGIN) {
    dx = VIEWPORT_MARGIN - bubbleRect.left;
  } else if (bubbleRect.right > window.innerWidth - VIEWPORT_MARGIN) {
    dx = window.innerWidth - VIEWPORT_MARGIN - bubbleRect.right;
  }

  if (bubbleRect.top < VIEWPORT_MARGIN) {
    dy = VIEWPORT_MARGIN - bubbleRect.top;
  } else if (bubbleRect.bottom > window.innerHeight - VIEWPORT_MARGIN) {
    dy = window.innerHeight - VIEWPORT_MARGIN - bubbleRect.bottom;
  }

  if (dx === 0 && dy === 0) {
    return coords;
  }

  return {
    top: coords.top + dy,
    left: coords.left + dx,
  };
}

export function LiksTooltip({
  content,
  placement = 'right',
  block = false,
  describeChildren = false,
  className,
  children,
}: LiksTooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<TooltipCoords | null>(null);
  const hasContent = content !== undefined && content !== '';

  // Sidebar (and similar) clears content while expanded. Reset open state during
  // render so re-enabling content cannot resurrect a stale portal — no effect.
  if (!hasContent && isOpen) {
    setIsOpen(false);
    setCoords(null);
  }

  const closeTooltip = useCallback(() => {
    setIsOpen(false);
    setCoords(null);
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    const nextCoords = getTooltipCoords(
      trigger.getBoundingClientRect(),
      placement,
    );
    const bubble = bubbleRef.current;

    if (!bubble) {
      setCoords(nextCoords);
      return;
    }

    // Apply candidate coords first via state; clamping happens after paint.
    setCoords(nextCoords);
  }, [placement]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    updatePosition();
  }, [isOpen, updatePosition, content]);

  useLayoutEffect(() => {
    if (!isOpen || coords === null || !bubbleRef.current) {
      return;
    }

    const clamped = getClampedCoords(
      coords,
      bubbleRef.current.getBoundingClientRect(),
    );

    if (clamped.top !== coords.top || clamped.left !== coords.left) {
      setCoords(clamped);
    }
  }, [isOpen, coords, content, placement]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleReposition = () => {
      updatePosition();
    };

    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    const trigger = triggerRef.current;
    const resizeObserver = new ResizeObserver(handleReposition);
    if (trigger !== null) {
      resizeObserver.observe(trigger);
    }

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
      resizeObserver.disconnect();
    };
  }, [isOpen, updatePosition]);

  if (!hasContent) {
    return children;
  }

  const classNames = [
    'liks-tooltip',
    `liks-tooltip--${placement}`,
    block && 'liks-tooltip--block',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const accessibilityProps = describeChildren
    ? { role: 'img' as const, 'aria-label': content }
    : {};

  const openTooltip = () => {
    setIsOpen(true);
  };

  return (
    <>
      <span
        ref={triggerRef}
        className={classNames}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
        onFocusCapture={openTooltip}
        onBlurCapture={(event) => {
          if (
            event.relatedTarget instanceof Node &&
            event.currentTarget.contains(event.relatedTarget)
          ) {
            return;
          }
          closeTooltip();
        }}
        {...accessibilityProps}
      >
        {children}
      </span>
      {isOpen &&
        coords !== null &&
        createPortal(
          <span
            ref={bubbleRef}
            className={`liks-tooltip__bubble liks-tooltip__bubble--${placement}`}
            style={{ top: coords.top, left: coords.left }}
            aria-hidden
          >
            {content}
          </span>,
          document.body,
        )}
    </>
  );
}
